import {
  GRANT_CREDITS_MAX,
  grantCreditsSchema,
  type GrantCreditsInput,
} from '@remote-medium/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  Textarea,
  errorMessage,
} from '@remote-medium/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

// Curator grant of role credits without a Polar order. Who and when are
// recorded (rule 8); the note is optional.
export function GrantCredits({
  companyName,
  suggested = 1,
  variant = 'outline',
  onGrant,
}: {
  companyName: string;
  /** Pre-filled amount, e.g. how many roles are waiting on a credit. */
  suggested?: number;
  variant?: 'default' | 'outline';
  onGrant: (input: GrantCreditsInput) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const defaults = { credits: Math.min(Math.max(suggested, 1), GRANT_CREDITS_MAX), note: '' };
  const { register, handleSubmit, reset, formState } = useForm<GrantCreditsInput>({
    resolver: zodResolver(grantCreditsSchema),
    defaultValues: defaults,
  });
  const errors = formState.errors;
  const busy = formState.isSubmitting;

  const submit = handleSubmit(async (values) => {
    setError(null);
    try {
      await onGrant(values);
      setOpen(false);
    } catch (e) {
      setError(errorMessage(e));
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (next) reset(defaults);
        setOpen(next);
        setError(null);
      }}
    >
      <DialogTrigger render={<Button variant={variant} size="sm" />}>
        <Plus data-icon="inline-start" />
        Grant credits
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Grant role credits to {companyName}</DialogTitle>
            <DialogDescription>
              No payment is taken. They are used like bought credits, and you are recorded as the
              curator who granted them.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="grant-credits">Credits</FieldLabel>
              <Input
                id="grant-credits"
                type="text"
                inputMode="numeric"
                aria-invalid={!!errors.credits}
                className="w-20 tabular-nums"
                {...register('credits', { setValueAs: Number })}
              />
              {errors.credits && (
                <FieldError errors={[{ message: `Between 1 and ${GRANT_CREDITS_MAX} credits` }]} />
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="grant-note">Note (optional)</FieldLabel>
              <Textarea
                id="grant-note"
                rows={3}
                aria-invalid={!!errors.note}
                placeholder="Partner deal, goodwill, a purchase that failed…"
                {...register('note')}
              />
              {errors.note && <FieldError errors={[errors.note]} />}
            </Field>
          </FieldGroup>
          {error && <p className="m-0 text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" disabled={busy} />}>
              Cancel
            </DialogClose>
            <Button type="submit" disabled={busy}>
              {busy ? 'Working…' : 'Grant credits'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
